import { memo, useId } from 'react';
import { cn } from '@kidshome/ui';
import { heroPluginFor } from '@kidshome/hero-content/art';
import { type EquippedLook } from '@/entities/cosmetics';
import '@/entities/hero/HeroAvatar.css';

/** An empty look — a bare avatar (the default when nothing is equipped). */
const NO_LOOK: EquippedLook = {};

/** The size ladder (px) for the circular avatar — sm: nav/list rows, md: cards, lg: headers. */
const SIZE_PX = { sm: 28, md: 44, lg: 80 } as const;

/** A bare px when a caller needs the bust to fit an exact slot (e.g. inside a bond ring). */
type AvatarSize = keyof typeof SIZE_PX | number;
const sizePx = (size: AvatarSize): number => (typeof size === 'number' ? size : SIZE_PX[size]);

/**
 * THE HERO AVATAR (ADR-0098/0099) — a small, circular BUST of the hero wearing its identity cosmetics,
 * reused wherever a compact hero identity belongs (nav, list rows, the hero chooser…). Hero-AGNOSTIC: it
 * masks a circle around whatever the hero's render plugin draws for the bust ({@link heroPluginFor}
 * `.renderAvatar`) — the princess plugin crops to her face + composes head/face cosmetics (so equipping a
 * hat in the wardrobe updates every avatar with no second asset); any other archetype gets the neutral
 * procedural sprite. The equipped SCENE is deliberately NOT drawn (a busy backdrop destroys legibility at
 * 28px). Decorative (`aria-hidden`); the consuming row carries the accessible name.
 *
 * Lives in `entities/hero` (not the cosmetics-shop widget) so any widget/page can show a hero identity
 * without a cross-widget import (ADR FSD layering) — it depends only on the hero render plugin + the
 * cosmetics `EquippedLook` type.
 */
export const HeroAvatar = memo(function HeroAvatar({
  archetypeId,
  stageIndex,
  look = NO_LOOK,
  size = 'md',
  ring = false,
  className,
}: {
  archetypeId: string | null;
  stageIndex: number;
  look?: EquippedLook;
  size?: AvatarSize;
  /** A decorative selection ring (e.g. the hero chooser) — purely visual. */
  ring?: boolean;
  className?: string;
}): React.ReactElement {
  const px = sizePx(size);
  const style = { width: px, height: px } as React.CSSProperties;
  const uid = useId().replace(/[^a-zA-Z0-9_-]/g, '');

  return (
    <span
      className={cn('hero-avatar', ring && 'has-ring', className)}
      style={style}
      aria-hidden="true"
    >
      {heroPluginFor(archetypeId).renderAvatar({ stageIndex, look, uid })}
    </span>
  );
});
